// Reflective Journal → one document for submission: pure helpers shared by the app and tests (no imports). Reflections
// are ordered by lecture date (then title); each keeps its title, date and focus, its six prompts (or its written-up
// note, headings moved down a level), and its sources. The result is a note-shaped document, so Dawwin's existing Word,
// PDF and Markdown export can produce the file.
type Node={type:string;attrs?:Record<string,unknown>;content?:Node[];text?:string;marks?:unknown[]}
export type ExportReflection={id:string;session_date:string;session_title:string;session_label:string;session_focus:string;content_document_id:string;[field:string]:unknown}
export type ExportSource={title:string;author:string;year:string;doi:string;url:string;citation:string}
export type JournalExportInput={title:string;word_count_target:number|null;deadline:string;reflections:ExportReflection[];prompts:{field:string;label:string}[];rich:Map<string,unknown>;sources:Map<string,ExportSource[]>}

const t=(text:string):Node=>({type:'text',text})
const para=(text:string,italic=false):Node=>({type:'paragraph',content:text?[italic?{...t(text),marks:[{type:'italic'}]}:t(text)]:[]})
const heading=(level:number,text:string):Node=>({type:'heading',attrs:{level},content:[t(text)]})
const words=(s:string)=>(s.match(/[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu)||[]).length
const plain=(n:Node):string=>n.text??(n.content||[]).map(plain).join(n.type==='doc'?'\n':' ')
export const reflectionHeading=(r:ExportReflection)=>r.session_title?.trim()||r.session_label?.trim()||(r.session_date?`Reflection, ${longDate(r.session_date)}`:'Reflection')
export function longDate(iso:string){
 const m=/^(\d{4})-(\d{2})-(\d{2})/.exec(iso||'')
 if(!m)return iso||''
 const months=['January','February','March','April','May','June','July','August','September','October','November','December']
 return`${Number(m[3])} ${months[Number(m[2])-1]} ${m[1]}`
}
/** By lecture date, undated last, then by title. */
export const byLectureDate=(a:ExportReflection,b:ExportReflection)=>(a.session_date||'9999').localeCompare(b.session_date||'9999')||reflectionHeading(a).localeCompare(reflectionHeading(b),undefined,{numeric:true})
export function citation(s:ExportSource){
 if(s.citation?.trim())return s.citation.trim()
 return[s.author&&`${s.author}${s.year?` (${s.year})`:''}.`,!s.author&&s.year&&`(${s.year}).`,s.title&&`${s.title}.`,s.doi?`https://doi.org/${s.doi.replace(/^https?:\/\/(dx\.)?doi\.org\//,'')}`:s.url].filter(Boolean).join(' ')
}
/** A written-up reflection's blocks: its own title line dropped if it repeats the heading, headings two levels down. */
function richBlocks(content:unknown,title:string):Node[]{
 let doc=content as Node|null
 if(typeof content==='string'){try{doc=JSON.parse(content)}catch{return content.trim()?[para(content)]:[]}}
 const blocks=[...(doc?.content||[])]
 if(blocks[0]&&plain(blocks[0]).trim()===title.trim())blocks.shift()
 const demote=(n:Node):Node=>n.type==='heading'?{...n,attrs:{...n.attrs,level:Math.min(6,Number(n.attrs?.level||1)+2)}}:n.content?{...n,content:n.content.map(demote)}:n
 return blocks.map(demote)
}
export function buildJournalDoc(input:JournalExportInput):{doc:Node;reflections:number;words:number}{
 const ordered=[...input.reflections].sort(byLectureDate)
 const body:Node[]=[]
 let total=0
 for(const r of ordered){
  const title=reflectionHeading(r),part:Node[]=[]
  part.push(heading(2,title))
  const meta=[r.session_date?longDate(r.session_date):'',r.session_focus?.trim()].filter(Boolean).join(' · ')
  if(meta)part.push(para(meta,true))
  const content:Node[]=[]
  if(r.content_document_id){
   const rich=input.rich.get(r.id)
   if(rich==null)content.push(para('[This reflection’s written-up note could not be read for the export.]',true))
   else content.push(...richBlocks(rich,title))
  }else for(const p of input.prompts){
   const answer=String(r[p.field]??'').trim()
   if(!answer)continue
   content.push(heading(3,p.label),...answer.split(/\n+/).map(s=>para(s.trim())).filter(n=>n.content!.length))
  }
  total+=content.filter(n=>n.type!=='heading').reduce((n,b)=>n+words(plain(b)),0)
  part.push(...content)
  const sources=(input.sources.get(r.id)||[]).map(citation).filter(Boolean)
  if(sources.length)part.push(heading(3,'Sources'),...sources.map(s=>para(s)))
  body.push(...part)
 }
 const summary=[`${ordered.length} reflection${ordered.length===1?'':'s'}`,`${total.toLocaleString('en-GB')} words${input.word_count_target?` (target ${input.word_count_target.toLocaleString('en-GB')})`:''}`,input.deadline?`due ${longDate(input.deadline)}`:''].filter(Boolean).join(' · ')
 return{doc:{type:'doc',content:[heading(1,input.title||'Reflective Journal'),para(summary,true),...body]},reflections:ordered.length,words:total}
}
